import React, { useEffect, useRef, useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Alert,
  TextField,
  Button,
  Grid,
  InputAdornment,
  CircularProgress,
  FormControlLabel,
  Switch,
} from '@mui/material';
import PaletteIcon from '@mui/icons-material/Palette';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useTranslation } from 'react-i18next';
import {
  getGeneralConfig,
  saveGeneralConfig,
  getHeaderConfig,
  getFooterSettings,
  saveFooterSettings,
  getAboutSettings,
  saveAboutSettings,
  getServiceSettings,
  saveServiceSettings,
  getReferenceSettings,
  saveReferenceSettings,
  getProductSolutionSettings,
  saveProductSolutionSettings,
  getContactSettings,
  saveContactSettings,
  getRestaurantSettings,
  saveRestaurantSettings,
  getCartSettings,
  saveCartSettings,
} from '../../../services/ecommerceApi';
import { notifyEcommerceSaved } from '../utils/ecommerceSaveToast';
import {
  deriveSitePalette,
  extractLogoPalette,
  getContrastText,
  patchHeroSettingsJson,
  resolveHeroBackground,
  type SitePalette,
} from '../utils/logoColor';
import {
  resolveSectionSettingsFromSource,
  serializeSectionSettingsJson,
} from '../utils/corporateSectionSettings';

interface BrandColorSettingsProps {
  companyId: string | null;
}

const DEFAULT_PRIMARY = '#2563eb';
const DEFAULT_SECONDARY = '#1d4ed8';

// Hazır marka renkleri (primary + hover).
const BRAND_PRESETS = [
  { key: 'orange', primary: '#f27a1a', hover: '#e06d12' },
  { key: 'navy', primary: '#1e3a5f', hover: '#162d4a' },
  { key: 'blue', primary: '#2563eb', hover: '#1d4ed8' },
  { key: 'indigo', primary: '#4f46e5', hover: '#4338ca' },
  { key: 'purple', primary: '#7c3aed', hover: '#6d28d9' },
  { key: 'rose', primary: '#e11d48', hover: '#be123c' },
  { key: 'red', primary: '#dc2626', hover: '#b91c1c' },
  { key: 'teal', primary: '#0d9488', hover: '#0f766e' },
  { key: 'green', primary: '#15803d', hover: '#166534' },
  { key: 'amber', primary: '#d97706', hover: '#b45309' },
  { key: 'slate', primary: '#475569', hover: '#334155' },
  { key: 'black', primary: '#111111', hover: '#000000' },
] as const;

const normalizeHex = (value: string): string => {
  const v = (value || '').trim();
  if (!v) return '';
  return v.startsWith('#') ? v : `#${v}`;
};

const isValidHex = (value: string): boolean => /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value);

/** API entity/DTO alanlarını camel/Pascal fark etmeksizin okur. */
const pickField = (obj: Record<string, any> | null | undefined, ...keys: string[]): any => {
  if (!obj) return undefined;
  for (const key of keys) {
    if (obj[key] != null && obj[key] !== '') return obj[key];
  }
  const lowerMap = Object.keys(obj).reduce<Record<string, string>>((acc, k) => {
    acc[k.toLowerCase()] = k;
    return acc;
  }, {});
  for (const key of keys) {
    const found = lowerMap[key.toLowerCase()];
    if (found != null && obj[found] != null && obj[found] !== '') return obj[found];
  }
  return undefined;
};

const BrandColorSettings: React.FC<BrandColorSettingsProps> = ({ companyId }) => {
  const { t } = useTranslation('salesSystem');
  const [primary, setPrimary] = useState(DEFAULT_PRIMARY);
  const [secondary, setSecondary] = useState(DEFAULT_SECONDARY);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadedRef = useRef(false);

  // Logodan öneri + footer senkron
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [configId, setConfigId] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<string[]>([]);
  const [selectedBase, setSelectedBase] = useState<string | null>(null);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestError, setSuggestError] = useState<string | null>(null);
  const [syncFooter, setSyncFooter] = useState(true);
  const [syncHero, setSyncHero] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!companyId) return;
      setLoading(true);
      try {
        const [response, headerResponse] = await Promise.all([
          getGeneralConfig(companyId),
          getHeaderConfig(companyId).catch(() => null),
        ]);
        if (response?.data) {
          setPrimary(response.data.primaryColor || DEFAULT_PRIMARY);
          setSecondary(response.data.secondaryColor || DEFAULT_SECONDARY);
          const resolvedId = pickField(response.data as any, 'id', 'Id');
          if (resolvedId) setConfigId(String(resolvedId));
        }
        const headerLogo = headerResponse?.data?.logoUrl;
        if (headerLogo) setLogoUrl(headerLogo);
        loadedRef.current = true;
      } catch (error) {
        console.error('❌ [BRAND-COLOR] Ayarlar yüklenemedi:', error);
        setFeedback({ type: 'error', message: t('ecommerceGeneral.brandColor.loadError') });
      } finally {
        setLoading(false);
      }
    };

    load();

    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [companyId, t]);

  // Logo değişince baskın renk adaylarını arka planda çıkar (öneri; otomatik uygulama yok).
  useEffect(() => {
    let cancelled = false;
    if (!logoUrl) {
      setCandidates([]);
      setSelectedBase(null);
      return;
    }
    setSuggesting(true);
    setSuggestError(null);
    extractLogoPalette(logoUrl, 5)
      .then((colors) => {
        if (!cancelled) {
          setCandidates(colors);
          setSelectedBase(colors[0] || null);
        }
      })
      .catch((err) => {
        console.warn('⚠️ [BRAND-COLOR] Logodan renk çıkarılamadı:', err);
        if (!cancelled) {
          setCandidates([]);
          setSelectedBase(null);
          setSuggestError(t('ecommerceGeneral.brandColor.logoSuggest.extractError'));
        }
      })
      .finally(() => {
        if (!cancelled) setSuggesting(false);
      });
    return () => {
      cancelled = true;
    };
  }, [logoUrl, t]);

  // Footer arka plan / yazı rengini site rengine göre günceller.
  // Storefront footer, renkleri hem üst düzey alanlardan hem de elementStylesJson'dan
  // okuduğu için ikisini birlikte güncelleriz; diğer footer ayarları korunur.
  const updateFooterColors = async (palette: SitePalette, resolvedConfigId: string) => {
    if (!companyId || !resolvedConfigId) return;
    try {
      const res = await getFooterSettings(companyId, resolvedConfigId);
      const current = res?.data;
      if (!current) return; // Footer kaydı yoksa dokunma.

      let elementStyles: Record<string, any> = {};
      const rawStyles = pickField(current, 'elementStylesJson');
      if (rawStyles) {
        try {
          elementStyles = typeof rawStyles === 'string' ? JSON.parse(rawStyles) || {} : rawStyles;
        } catch {
          elementStyles = {};
        }
      }

      // Footer arka planı (storefront öncelikle bunu okur)
      elementStyles.backgroundColor = {
        ...(elementStyles.backgroundColor || {}),
        backgroundColor: palette.footerBackground,
      };

      // Metin renklerini site rengine bağla (başlıklar solid, gövde yumuşatılmış)
      const setElementColor = (key: string, color: string) => {
        elementStyles[key] = { ...(elementStyles[key] || {}), color };
      };
      setElementColor('companyName', palette.footerHeading);
      setElementColor('sectionTitle', palette.footerHeading);
      setElementColor('companyDescription', palette.footerMuted);
      setElementColor('contactInfo', palette.footerMuted);
      setElementColor('quickLinks', palette.footerLink);
      setElementColor('copyright', palette.footerMuted);

      await saveFooterSettings({
        ...current,
        companyId,
        configId: resolvedConfigId,
        backgroundColor: palette.footerBackground,
        bottomBarBackgroundColor: palette.footerBackground,
        textColor: palette.footerText,
        linkColor: palette.footerLink,
        elementStylesJson: JSON.stringify(elementStyles),
      });
    } catch (error) {
      console.error('❌ [BRAND-COLOR] Footer renkleri güncellenemedi:', error);
    }
  };

  const resolveConfigId = async (): Promise<string | null> => {
    if (configId) return configId;
    if (!companyId) return null;
    try {
      const response = await getGeneralConfig(companyId);
      const id = pickField(response?.data as any, 'id', 'Id');
      if (id) {
        const resolved = String(id);
        setConfigId(resolved);
        return resolved;
      }
    } catch (error) {
      console.error('❌ [BRAND-COLOR] ConfigId alınamadı:', error);
    }
    return null;
  };

  /** Kurumsal sayfa hero kayıtlarını (references/product-solutions) site rengine bağlar. */
  const updateCorporateHeroPage = async (
    label: string,
    getter: (companyId: string, configId: string) => Promise<any>,
    saver: (data: any) => Promise<any>,
    palette: SitePalette,
    resolvedConfigId: string
  ): Promise<boolean> => {
    if (!companyId) return false;
    try {
      const res = await getter(companyId, resolvedConfigId);
      const current = res?.data;
      if (!current) return false;

      const nextBg = resolveHeroBackground(pickField(current, 'heroBackgroundColor'), palette);
      await saver({
        companyId,
        configId: resolvedConfigId,
        heroTitle: pickField(current, 'heroTitle'),
        heroDescription: pickField(current, 'heroDescription'),
        heroBackgroundColor: nextBg,
        heroHeight: pickField(current, 'heroHeight') ?? 500,
        heroTemplateType: pickField(current, 'heroTemplateType') || 'classic-gradient',
        heroSettingsJson: patchHeroSettingsJson(pickField(current, 'heroSettingsJson'), palette, nextBg),
        itemsPerRow: pickField(current, 'itemsPerRow'),
        showCompanyName: pickField(current, 'showCompanyName'),
        showDescription: pickField(current, 'showDescription'),
        showCategory: pickField(current, 'showCategory'),
        logoSize: pickField(current, 'logoSize'),
        sectionSettingsJson: pickField(current, 'sectionSettingsJson'),
      });
      return true;
    } catch (error) {
      console.warn(`⚠️ [BRAND-COLOR] ${label} hero güncellenemedi:`, error);
      return false;
    }
  };

  /** Hakkımızda: hero + istatistik paneli arka planı (minimal PATCH — diğer alanlar korunur). */
  const updateAboutSurfaces = async (
    palette: SitePalette,
    resolvedConfigId: string
  ): Promise<boolean> => {
    if (!companyId) return false;
    try {
      const res = await getAboutSettings(companyId, resolvedConfigId);
      const current = res?.data;
      if (!current) {
        console.warn('⚠️ [BRAND-COLOR] About ayarı bulunamadı, atlandı');
        return false;
      }

      const nextHeroBg = resolveHeroBackground(pickField(current, 'heroBackgroundColor'), palette);
      const sectionSettings = resolveSectionSettingsFromSource(current);
      const nextSectionSettings = {
        ...sectionSettings,
        storySection: {
          ...(sectionSettings.storySection || {}),
          statsBackgroundColor: palette.heroGradient,
        },
      };

      await saveAboutSettings({
        companyId,
        configId: resolvedConfigId,
        heroBackgroundColor: nextHeroBg,
        heroSettingsJson: patchHeroSettingsJson(
          pickField(current, 'heroSettingsJson'),
          palette,
          nextHeroBg
        ),
        sectionSettingsJson: serializeSectionSettingsJson(nextSectionSettings),
      });
      console.log('✅ [BRAND-COLOR] About hero + istatistik paneli güncellendi', {
        hero: nextHeroBg,
        stats: palette.heroGradient,
      });
      return true;
    } catch (error) {
      console.error('❌ [BRAND-COLOR] About yüzeyleri güncellenemedi:', error);
      return false;
    }
  };

  /**
   * Hizmetler: hero + Hizmetlerimiz (values) + CTA.
   * Entity spread etmeden temiz payload gönderir (ValuesBackgroundColor MaxLength=50 → solid hex).
   */
  const updateServicesSurfaces = async (
    palette: SitePalette,
    resolvedConfigId: string
  ): Promise<boolean> => {
    if (!companyId) return false;
    try {
      const res = await getServiceSettings(companyId, resolvedConfigId);
      const current = res?.data;
      if (!current) {
        console.warn('⚠️ [BRAND-COLOR] Service ayarı bulunamadı, atlandı');
        return false;
      }

      const nextHeroBg = resolveHeroBackground(pickField(current, 'heroBackgroundColor'), palette);
      // ValuesBackgroundColor DB MaxLength(50) — gradient sığmaz, solid marka tonu kullan.
      const nextValuesBg = palette.heroSolid;
      const nextCtaBg = palette.heroGradient;
      const nextCtaText = palette.heroTitleOnDark;

      const payload = {
        configId: resolvedConfigId,
        companyId,
        heroTitle: pickField(current, 'heroTitle'),
        heroDescription: pickField(current, 'heroDescription'),
        heroBackgroundColor: nextHeroBg,
        heroHeight: pickField(current, 'heroHeight') ?? 500,
        heroTemplateType: pickField(current, 'heroTemplateType') || 'classic-gradient',
        heroSettingsJson: patchHeroSettingsJson(
          pickField(current, 'heroSettingsJson'),
          palette,
          nextHeroBg
        ),
        storyTitle: pickField(current, 'storyTitle'),
        storyDescription: pickField(current, 'storyDescription'),
        storyBackgroundColor: pickField(current, 'storyBackgroundColor'),
        storyStatCustomers: pickField(current, 'storyStatCustomers'),
        storyStatProducts: pickField(current, 'storyStatProducts'),
        storyStatSupport: pickField(current, 'storyStatSupport'),
        storyStatSatisfaction: pickField(current, 'storyStatSatisfaction'),
        sectionSettingsJson: pickField(current, 'sectionSettingsJson'),
        valuesTitle: pickField(current, 'valuesTitle'),
        valuesDescription: pickField(current, 'valuesDescription'),
        valuesBackgroundColor: nextValuesBg,
        valuesJson: pickField(current, 'valuesJson'),
        whyChooseTitle: pickField(current, 'whyChooseTitle'),
        whyChooseDescription: pickField(current, 'whyChooseDescription'),
        whyChooseBackgroundColor: pickField(current, 'whyChooseBackgroundColor'),
        featuresJson: pickField(current, 'featuresJson'),
        ctaEnabled: pickField(current, 'ctaEnabled') ?? true,
        ctaTitle: pickField(current, 'ctaTitle'),
        ctaDescription: pickField(current, 'ctaDescription'),
        ctaBackgroundColor: nextCtaBg,
        ctaTextColor: nextCtaText,
        ctaPrimaryButtonEnabled: pickField(current, 'ctaPrimaryButtonEnabled') ?? true,
        ctaPrimaryButtonText: pickField(current, 'ctaPrimaryButtonText'),
        ctaPrimaryButtonAction: pickField(current, 'ctaPrimaryButtonAction'),
        ctaSecondaryButtonEnabled: pickField(current, 'ctaSecondaryButtonEnabled') ?? true,
        ctaSecondaryButtonText: pickField(current, 'ctaSecondaryButtonText'),
        ctaSecondaryButtonAction: pickField(current, 'ctaSecondaryButtonAction'),
        serviceSubMenusJson: pickField(current, 'serviceSubMenusJson'),
      };

      const saveRes = await saveServiceSettings(payload);
      if (saveRes?.success === false) {
        throw new Error(saveRes?.message || 'Service save failed');
      }
      console.log('✅ [BRAND-COLOR] Services hero + Hizmetlerimiz + CTA güncellendi', {
        values: nextValuesBg,
        cta: nextCtaBg,
      });
      return true;
    } catch (error) {
      console.error('❌ [BRAND-COLOR] Services yüzeyleri güncellenemedi:', error);
      return false;
    }
  };

  /** İletişim form / harita / bilgi kartı renklerini Genel site rengine bağlar. */
  const updateContactBrandColors = async (palette: SitePalette, resolvedConfigId: string) => {
    if (!companyId || !resolvedConfigId) return false;
    try {
      const res = await getContactSettings(companyId, resolvedConfigId);
      const current = res?.data;
      if (!current) return false;
      const nextBg = resolveHeroBackground(pickField(current, 'heroBackgroundColor'), palette);
      const rawJson = pickField(current, 'settingsJson', 'SettingsJson');
      let parsed: Record<string, any> = {};
      try {
        parsed =
          typeof rawJson === 'string'
            ? (JSON.parse(rawJson || '{}') as Record<string, any>)
            : ((rawJson || {}) as Record<string, any>);
      } catch {
        parsed = {};
      }
      await saveContactSettings({
        companyId,
        configId: resolvedConfigId,
        heroEnabled: pickField(current, 'heroEnabled') ?? true,
        contactInfoEnabled: pickField(current, 'contactInfoEnabled'),
        formEnabled: pickField(current, 'formEnabled'),
        heroTitle: pickField(current, 'heroTitle'),
        heroDescription: pickField(current, 'heroDescription'),
        heroBackgroundColor: nextBg,
        contactAddress: pickField(current, 'contactAddress'),
        contactPhone: pickField(current, 'contactPhone'),
        contactEmail: pickField(current, 'contactEmail'),
        contactWorkingHours: pickField(current, 'contactWorkingHours'),
        mapEnabled: pickField(current, 'mapEnabled'),
        mapLatitude: pickField(current, 'mapLatitude'),
        mapLongitude: pickField(current, 'mapLongitude'),
        mapZoom: pickField(current, 'mapZoom'),
        mapHeight: pickField(current, 'mapHeight'),
        mapWidth: pickField(current, 'mapWidth'),
        mapTitle: pickField(current, 'mapTitle'),
        formSlug: pickField(current, 'formSlug'),
        templateType: pickField(current, 'templateType'),
        settingsJson: JSON.stringify({
          ...parsed,
          design: {
            ...(parsed.design || {}),
            primaryColor: palette.primary,
            accentColor: palette.primary,
            accentGradientFrom: palette.primary,
            accentGradientTo: palette.hover,
            sectionBackgroundColor: palette.footerBackground,
          },
        }),
      });
      return true;
    } catch (error) {
      console.warn('⚠️ [BRAND-COLOR] Contact renkleri güncellenemedi:', error);
      return false;
    }
  };

  // Hero + bağlı yüzeyleri site rengine bağlar.
  const updateHeroColors = async (palette: SitePalette, resolvedConfigId: string) => {
    if (!companyId || !resolvedConfigId) {
      return { about: false, services: false, others: 0 };
    }

    const [aboutOk, servicesOk, refOk, productOk, branchOk] = await Promise.all([
      updateAboutSurfaces(palette, resolvedConfigId),
      updateServicesSurfaces(palette, resolvedConfigId),
      updateCorporateHeroPage(
        'References',
        getReferenceSettings,
        saveReferenceSettings,
        palette,
        resolvedConfigId
      ),
      updateCorporateHeroPage(
        'ProductSolutions',
        getProductSolutionSettings,
        saveProductSolutionSettings,
        palette,
        resolvedConfigId
      ),
      (async () => {
        try {
          const res = await getRestaurantSettings(companyId, resolvedConfigId);
          const current = res?.data;
          await saveRestaurantSettings({
            configId: resolvedConfigId,
            heroEnabled: pickField(current, 'heroEnabled') !== false,
            heroTitle: pickField(current, 'heroTitle') || 'Şubeler',
            heroDescription:
              pickField(current, 'heroDescription') || 'Şehirlere göre şubelerimizi keşfedin.',
            heroBackgroundColor: palette.heroSolid,
          });
          return true;
        } catch (error) {
          console.warn('⚠️ [BRAND-COLOR] Branch hero güncellenemedi:', error);
          return false;
        }
      })(),
    ]);

    return {
      about: aboutOk,
      services: servicesOk,
      others: [refOk, productOk, branchOk].filter(Boolean).length,
    };
  };

  // Sepet / sipariş buton ve tutar renklerini site rengine bağlar.
  const updateCartColors = async (palette: SitePalette, resolvedConfigId: string) => {
    if (!companyId || !resolvedConfigId) return;
    try {
      const res = await getCartSettings(companyId, resolvedConfigId);
      const current = res?.data;
      if (!current) return;

      const parseJson = (raw: unknown, fallback: Record<string, any> = {}): Record<string, any> => {
        if (!raw) return fallback;
        if (typeof raw === 'object') return raw as Record<string, any>;
        try {
          return JSON.parse(String(raw)) || fallback;
        } catch {
          return fallback;
        }
      };

      const completeOrderButton = parseJson(
        pickField(current, 'completeOrderButtonJson', 'CompleteOrderButtonJson')
      );
      const orderSummary = parseJson(pickField(current, 'orderSummaryJson', 'OrderSummaryJson'));
      const productCard = parseJson(pickField(current, 'productCardJson', 'ProductCardJson'));

      completeOrderButton.backgroundColor = palette.primary;
      completeOrderButton.hoverBackgroundColor = palette.hover;

      const defaultBlues = new Set(['#2563eb', '#1d4ed8', '#3b82f6', '#1976d2']);
      const isDefaultBlue = (color?: string) =>
        !color || defaultBlues.has(String(color).trim().toLowerCase());

      orderSummary.total = {
        ...(orderSummary.total || {}),
        color: palette.primary,
      };

      if (productCard.price && isDefaultBlue(productCard.price.color)) {
        productCard.price = {
          ...productCard.price,
          color: palette.primary,
        };
      }

      await saveCartSettings({
        id: pickField(current, 'id', 'Id'),
        configId: resolvedConfigId,
        companyId,
        orderType: pickField(current, 'orderType', 'OrderType'),
        pageTitle: pickField(current, 'pageTitle', 'PageTitle'),
        productCardJson: JSON.stringify(productCard),
        orderSummaryJson: JSON.stringify(orderSummary),
        shippingJson: pickField(current, 'shippingJson', 'ShippingJson'),
        completeOrderButtonJson: JSON.stringify(completeOrderButton),
        foodOrderSettingsJson: pickField(current, 'foodOrderSettingsJson', 'FoodOrderSettingsJson'),
      });
    } catch (error) {
      console.warn('⚠️ [BRAND-COLOR] Sepet renkleri güncellenemedi:', error);
    }
  };

  const persist = async (next: { primary: string; secondary: string }) => {
    if (!companyId) return;
    const primaryColor = normalizeHex(next.primary);
    const secondaryColor = normalizeHex(next.secondary);
    if (!isValidHex(primaryColor) || !isValidHex(secondaryColor)) return;

    setSaving(true);
    setFeedback(null);
    try {
      await saveGeneralConfig({ companyId, primaryColor, secondaryColor });
      const palette = deriveSitePalette(primaryColor);
      const resolvedId = await resolveConfigId();

      if (syncFooter && resolvedId) {
        await updateFooterColors(palette, resolvedId);
      }

      if (resolvedId) {
        await updateCartColors(palette, resolvedId);
        await updateContactBrandColors(palette, resolvedId);
      }

      let surfaceMsg = '';
      if (syncHero) {
        if (!resolvedId) {
          setFeedback({
            type: 'error',
            message: t('ecommerceGeneral.brandColor.syncHero.configMissing'),
          });
        } else {
          const result = await updateHeroColors(palette, resolvedId);
          const parts: string[] = [];
          if (result.services) parts.push(t('ecommerceGeneral.brandColor.syncHero.updatedServices'));
          if (result.about) parts.push(t('ecommerceGeneral.brandColor.syncHero.updatedAboutStats'));
          if (parts.length) {
            surfaceMsg = parts.join(' · ');
          } else {
            surfaceMsg = t('ecommerceGeneral.brandColor.syncHero.noRecords');
          }
        }
      }

      notifyEcommerceSaved(t('ecommerceGeneral.brandColor.saveSuccess'));
      if (surfaceMsg) {
        setFeedback({
          type: 'success',
          message: `${surfaceMsg} ${t('ecommerceGeneral.brandColor.syncHero.refreshHint')}`,
        });
      }
    } catch (error) {
      console.error('❌ [BRAND-COLOR] Ayar kaydedilemedi:', error);
      setFeedback({ type: 'error', message: t('ecommerceGeneral.brandColor.saveError') });
    } finally {
      setSaving(false);
    }
  };

  const schedulePersist = (next: { primary: string; secondary: string }) => {
    if (!loadedRef.current || !companyId) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => persist(next), 500);
  };

  const applyPreset = (preset: { primary: string; hover: string }) => {
    setPrimary(preset.primary);
    setSecondary(preset.hover);
    schedulePersist({ primary: preset.primary, secondary: preset.hover });
  };

  const applySuggestion = () => {
    if (!selectedBase) return;
    const palette = deriveSitePalette(selectedBase);
    setPrimary(palette.primary);
    setSecondary(palette.hover);
    // Debounce atlamadan hemen uygula (footer/hero/CTA senkronu için)
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    void persist({ primary: palette.primary, secondary: palette.hover });
  };

  const handlePrimaryChange = (value: string) => {
    setPrimary(value);
    schedulePersist({ primary: value, secondary });
  };

  const handleSecondaryChange = (value: string) => {
    setSecondary(value);
    schedulePersist({ primary, secondary: value });
  };

  const safePrimary = isValidHex(normalizeHex(primary)) ? normalizeHex(primary) : DEFAULT_PRIMARY;
  const safeSecondary = isValidHex(normalizeHex(secondary)) ? normalizeHex(secondary) : DEFAULT_SECONDARY;

  const suggestionPalette = selectedBase ? deriveSitePalette(selectedBase) : null;
  const suggestionApplied = Boolean(
    suggestionPalette && safePrimary.toLowerCase() === suggestionPalette.primary.toLowerCase()
  );

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <PaletteIcon sx={{ mr: 1.5, color: safePrimary }} />
        <Typography variant="h6">{t('ecommerceGeneral.brandColor.title')}</Typography>
        {saving && <CircularProgress size={18} sx={{ ml: 1.5 }} />}
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('ecommerceGeneral.brandColor.help')}
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          <Typography variant="subtitle2" gutterBottom>
            {t('ecommerceGeneral.brandColor.presetsLabel')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2.5 }}>
            {BRAND_PRESETS.map((preset) => {
              const selected = safePrimary.toLowerCase() === preset.primary.toLowerCase();
              return (
                <Button
                  key={preset.key}
                  size="small"
                  variant={selected ? 'contained' : 'outlined'}
                  disabled={!companyId}
                  onClick={() => applyPreset(preset)}
                  sx={{
                    textTransform: 'none',
                    borderColor: preset.primary,
                    color: selected ? '#fff' : preset.primary,
                    backgroundColor: selected ? preset.primary : 'transparent',
                    '&:hover': {
                      borderColor: preset.primary,
                      backgroundColor: selected ? preset.hover : `${preset.primary}14`,
                    },
                  }}
                  startIcon={
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        backgroundColor: preset.primary,
                        border: '1px solid rgba(0,0,0,0.12)',
                      }}
                    />
                  }
                >
                  {t(`ecommerceGeneral.brandColor.presets.${preset.key}`)}
                </Button>
              );
            })}
          </Box>

          <Box
            sx={{
              mb: 2.5,
              p: 2,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: '#fafafa',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <AutoAwesomeIcon fontSize="small" color="primary" sx={{ mr: 1 }} />
              <Typography variant="subtitle2">
                {t('ecommerceGeneral.brandColor.logoSuggest.title')}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {t('ecommerceGeneral.brandColor.logoSuggest.help')}
            </Typography>

            {!logoUrl ? (
              <Alert severity="info" sx={{ py: 0.5 }}>
                {t('ecommerceGeneral.brandColor.logoSuggest.noLogo')}
              </Alert>
            ) : suggesting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  {t('ecommerceGeneral.brandColor.logoSuggest.extracting')}
                </Typography>
              </Box>
            ) : suggestError ? (
              <Alert severity="warning" sx={{ py: 0.5 }}>
                {suggestError}
              </Alert>
            ) : suggestionPalette ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Box
                    component="img"
                    src={logoUrl}
                    alt="logo"
                    sx={{
                      width: 48,
                      height: 48,
                      objectFit: 'contain',
                      borderRadius: 1,
                      border: '1px solid #e0e0e0',
                      backgroundColor: '#fff',
                      p: 0.5,
                    }}
                  />
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      {t('ecommerceGeneral.brandColor.logoSuggest.chooseBase')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {candidates.map((color) => {
                        const active = selectedBase?.toLowerCase() === color.toLowerCase();
                        return (
                          <Box
                            key={color}
                            onClick={() => setSelectedBase(color)}
                            title={color}
                            sx={{
                              width: 34,
                              height: 34,
                              borderRadius: '50%',
                              backgroundColor: color,
                              cursor: 'pointer',
                              boxShadow: active ? '0 0 0 3px rgba(0,0,0,0.55)' : 'none',
                              border: '1px solid rgba(0,0,0,0.15)',
                              transition: 'box-shadow 120ms',
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {[
                      { color: suggestionPalette.primary, label: t('ecommerceGeneral.brandColor.logoSuggest.swatchPrimary') },
                      { color: suggestionPalette.hover, label: t('ecommerceGeneral.brandColor.logoSuggest.swatchHover') },
                      { color: suggestionPalette.footerBackground, label: t('ecommerceGeneral.brandColor.logoSuggest.swatchFooter') },
                      { color: suggestionPalette.heroGradient, label: t('ecommerceGeneral.brandColor.logoSuggest.swatchHero') },
                    ].map((s) => (
                      <Box key={s.label} sx={{ textAlign: 'center' }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 1,
                            background: s.color,
                            backgroundColor: s.color.includes('gradient') ? undefined : s.color,
                            border: '1px solid rgba(0,0,0,0.12)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: getContrastText(
                              s.color.includes('gradient') ? suggestionPalette.primary : s.color
                            ),
                            fontSize: 10,
                          }}
                        >
                          Aa
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {s.label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AutoAwesomeIcon />}
                    onClick={applySuggestion}
                    disabled={!companyId || suggestionApplied || saving}
                    sx={{ textTransform: 'none' }}
                  >
                    {suggestionApplied
                      ? t('ecommerceGeneral.brandColor.logoSuggest.applied')
                      : t('ecommerceGeneral.brandColor.logoSuggest.apply')}
                  </Button>
                </Box>
              </Box>
            ) : null}
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label={t('ecommerceGeneral.brandColor.primaryColor')}
                value={primary}
                onChange={(e) => handlePrimaryChange(e.target.value)}
                helperText={t('ecommerceGeneral.brandColor.primaryColorHelper')}
                disabled={!companyId}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box
                        component="input"
                        type="color"
                        value={safePrimary}
                        onChange={(e) => handlePrimaryChange(e.target.value)}
                        sx={{ border: 'none', width: 24, height: 24, p: 0, background: 'none', cursor: 'pointer' }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label={t('ecommerceGeneral.brandColor.hoverColor')}
                value={secondary}
                onChange={(e) => handleSecondaryChange(e.target.value)}
                helperText={t('ecommerceGeneral.brandColor.hoverColorHelper')}
                disabled={!companyId}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box
                        component="input"
                        type="color"
                        value={safeSecondary}
                        onChange={(e) => handleSecondaryChange(e.target.value)}
                        sx={{ border: 'none', width: 24, height: 24, p: 0, background: 'none', cursor: 'pointer' }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>

          <Box
            sx={{
              mt: 2.5,
              p: 2,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 2,
              backgroundColor: '#fafafa',
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
              {t('ecommerceGeneral.brandColor.previewLabel')}:
            </Typography>
            <Button
              variant="contained"
              disableElevation
              sx={{
                textTransform: 'none',
                backgroundColor: safePrimary,
                '&:hover': { backgroundColor: safeSecondary },
              }}
            >
              {t('ecommerceGeneral.brandColor.previewButton')}
            </Button>
            <Typography variant="body2" sx={{ color: safePrimary, fontWeight: 600, cursor: 'pointer' }}>
              {t('ecommerceGeneral.brandColor.previewLink')}
            </Typography>
          </Box>

          <FormControlLabel
            sx={{ mt: 1.5, alignItems: 'flex-start', display: 'flex' }}
            control={
              <Switch
                checked={syncFooter}
                onChange={(e) => setSyncFooter(e.target.checked)}
                disabled={!companyId}
                sx={{ mt: 0.25 }}
              />
            }
            label={
              <Box>
                <Typography variant="body2">
                  {t('ecommerceGeneral.brandColor.syncFooter.label')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('ecommerceGeneral.brandColor.syncFooter.help')}
                </Typography>
              </Box>
            }
          />

          <FormControlLabel
            sx={{ mt: 0.5, alignItems: 'flex-start', display: 'flex' }}
            control={
              <Switch
                checked={syncHero}
                onChange={(e) => setSyncHero(e.target.checked)}
                disabled={!companyId}
                sx={{ mt: 0.25 }}
              />
            }
            label={
              <Box>
                <Typography variant="body2">
                  {t('ecommerceGeneral.brandColor.syncHero.label')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('ecommerceGeneral.brandColor.syncHero.help')}
                </Typography>
              </Box>
            }
          />

          {feedback && (
            <Alert severity={feedback.type} sx={{ mt: 2 }}>
              {feedback.message}
            </Alert>
          )}
        </>
      )}
    </Paper>
  );
};

export default BrandColorSettings;
